import React, { useRef, useEffect, useState } from 'react';
import EndCallButton from './EndCallButton';
import ToggleAudioButton from './ToggleAudioButton';
import ToggleVideoButton from './ToggleVideoButton';
import ToggleFullScreenButton from './ToggleFullScreenButton';
import Options from './Options';
import Settings from './Settings';
import useRoomState from '../../hooks/useRoomState';
import { useTwilioAppState } from '../../state';
import { CONSUMER, PROVIDER } from '../../constants';
import useVideoContext from '../../hooks/useVideoContext';
import FlipCameraButton from './FlipCameraButton';
import ToggleScreenShareButton from './ToggleScreenShareButton';
import { getCallDuration, Logger, isMobile } from '../../utils';
import useFullScreenToggle from '../../hooks/useFullScreenToggle';
import Swipeable from './Swipeable';

export default function Controls(props) {
    const roomState = useRoomState();
    const isReconnecting = roomState === 'reconnecting';
    const { source } = useTwilioAppState();
    const { isPublishingTracks, isAcquiringLocalTracks, isSharingScreen } = useVideoContext();
    const enableScreenShare = window && window.serverData && window.serverData.data ? window.serverData.data.enableScreenShare : false;
    const guestLinkEnabled = window && window.serverData && window.serverData.data ? window.serverData.data.guestLinkEnabled : false;
    const timerRef = useRef();
    const [timeInSeconds, setTimeInSeconds] = useState("0:00");
    const [showDrawerUI, setShowDrawerUI] = useState(false);
    const isFirstRender = useRef(true);
    const [isFullScreen] = useFullScreenToggle();

    useEffect(() => {
        isFirstRender.current = false;
        if (source === PROVIDER) {
            timerRef.current = setInterval(showTime, 1000);
        }
        return () => {
            clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!isFirstRender.current && props.onDrawerStateChange)
            props.onDrawerStateChange(showDrawerUI, isFullScreen);
    }, [showDrawerUI, isFullScreen]);

    const onOptionsClicked = () => {
        Logger(`UI.Twilio.Room.DeviceSettings.Clicked.${props.deviceSettingsOpen ? "Close" : "Open"}`, new Date());
        props.toggleDeviceSettings();
    }

    const onAddGuestClicked = () => {
        Logger(`UI.Twilio.Room.AddGuest.Clicked.${props.showGuestLinkModal ? "Close" : "Open"}`, new Date());
        props.toggleGuestLinkModal();
    }

    const showTime = () => {
        if (window.patientInRoomEnterTime) {
            const callDuration = getCallDuration(window.patientInRoomEnterTime);
            setTimeInSeconds(callDuration);
        }
    }

    const handleTouch = (evt, dir, phase, swipetype, distance) => {
        if (dir === "up" || swipetype === "up")
            setShowDrawerUI(true);
        else if (dir === "down" || swipetype === "down")
            setShowDrawerUI(false);
    };

    const { localParticipantRole, providerButtons } = props;

    return (<>
        <div className="control-container">
            <Swipeable className="swipeable-drawer" handleTouch={handleTouch} threshold={500} restraint={100} allowedTime={500}><div className="drawer" onClick={() => { setShowDrawerUI(prevState => !prevState); }} /></Swipeable>

            {localParticipantRole && localParticipantRole.toLowerCase() !== 'staff' && source === PROVIDER && <div className="controls-left">{window.patientInRoomEnterTime && timeInSeconds ? timeInSeconds : "--:--"}</div>}
            <div className="controls-center">
                <ToggleVideoButton disabled={isReconnecting || isPublishingTracks || isAcquiringLocalTracks} showWaitingMsg={props.showWaitingMsg} />
                <FlipCameraButton disabled={isReconnecting || isPublishingTracks || isAcquiringLocalTracks} />
                <ToggleAudioButton disabled={isReconnecting || isPublishingTracks || isAcquiringLocalTracks} showWaitingMsg={props.showWaitingMsg} />
                {roomState !== 'disconnected' && (
                    <>
                        {source === CONSUMER && <EndCallButton setIsEndCall={props && props.setIsEndCall ? props.setIsEndCall : false} />}
                        {enableScreenShare && source === PROVIDER && <ToggleScreenShareButton disabled={isReconnecting} />}
                    </>
                )}
            </div>
            {roomState !== 'disconnected' &&
                <div className={`controls-right`}>
                    <ToggleFullScreenButton disabled={isReconnecting} />
                    {source === PROVIDER && guestLinkEnabled && <button type="button" id="add-guest-btn" className={`btn btn-sm ${props.showGuestLinkModal ? "active" : ""}`} onClick={() => onAddGuestClicked()}><span className="icon-add-guest" /></button>}
                    <div className="controls-divider" />
                </div>
            }
            <Options deviceSettingsOpen={props.deviceSettingsOpen} onOptionsClicked={onOptionsClicked} />
            {showDrawerUI && <div className="drawer-menu">
                <div className="action-item">
                    <Options deviceSettingsOpen={props.deviceSettingsOpen} onOptionsClicked={onOptionsClicked} showLabel />
                </div>
                {providerButtons && isFullScreen && source === PROVIDER &&
                    <div className="video-button-container">
                        {providerButtons}
                    </div>
            }
            </div>
            }
        </div>
        {props.deviceSettingsOpen && <Settings onOptionsClicked={onOptionsClicked} />}
        </>
    );
}